import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { ControlValueAccessor, FormBuilder, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BixiI18nService, IEditableI18n } from '@bixi/core/i18n';
import { logger } from '@bixi/core/internal';
import { NzSafeAny } from 'ng-zorro-antd/core/types/any';
import { NzSelectComponent } from 'ng-zorro-antd/select';
import { Observable, of, Subscription } from 'rxjs';
import {
  IEditableCol,
  IEditableRow,
  IEditableSelectCol,
  IEditableSelectOption,
  IEditableSelectOptions,
  IValidatorError
} from './editable-table.type';
import { EditableTemplateService } from './editable-template.service';

export type IOnTouchedType = () => NzSafeAny;
export type IOnChangeType = (value: NzSafeAny) => void;

@Component({
  selector: 'bixi-editable-table',
  exportAs: 'bixiEditableTable',
  host: { '[class.bixi-editable-table]': 'true' },
  templateUrl: 'editable-table.component.html',
  providers: [
    EditableTemplateService, {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BixiEditableTableComponent),
      multi: true
    }],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BixiEditableTableComponent implements ControlValueAccessor, OnInit, OnDestroy {
  isValid = true;
  form: FormGroup;
  editedRows: IEditableRow[] = []; // 编辑后的行
  editRow: IEditableRow; // 编辑中行
  cacheRow: IEditableRow | null = null; // 当前编辑行的初始数据
  editRowIndex: number | null = null; // 当前编辑行数
  rows: IEditableRow[] = [];
  addText: string;
  i18nSubscription = new Subscription();
  editTransitions: IEditableI18n;

  _cols: IEditableCol[] = [];
  @Input()
  get cols() {
    return this._cols;
  }
  set cols(cols: IEditableCol[]) {
    this._cols = cols.map((col) => {
      col = col as IEditableSelectCol;
      const { options } = col;
      if (options) {
        let _options: IEditableSelectOptions;
        if (!(options instanceof Observable)) {
          _options = of(options);
        } else {
          _options = options;
        }
        col.options = _options;
      }
      return col;
    });
  }
  @Input() scroll = { y: '300px' };
  @Input() draggable: boolean;
  // 拼写错误  下个版本移除
  @Input() dragable = false;
  @Input() footer?: TemplateRef<{}> | string | null;
  @Input() editable = true;
  @Input() validate: (fg: FormGroup, rows: IEditableRow[], row: IEditableRow | null) => { key: string; message: string }[];

  @Output() deleteRow: EventEmitter<IEditableRow> = new EventEmitter<IEditableRow>();
  @Output() addRow: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();
  @Output() dirtyRowsChange: EventEmitter<IEditableRow[]> = new EventEmitter<IEditableRow[]>();
  @Output() editChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('editContent') firstEditingEle: ElementRef | NzSelectComponent;

  onChange: IOnChangeType = () => { };
  onTouched: IOnTouchedType = () => { };

  constructor(
    private fb: FormBuilder,
    private i18n: BixiI18nService,
    private cdr: ChangeDetectorRef
  ) { }

  get rowDraggable() {
    // 兼容版本
    if (this.draggable === undefined) return !!this.dragable;
    return !!this.draggable;
  }

  get rowEditable() {
    if (this.isEditing) return true; // 可编辑状态下，返回 true
    return this.editable;
  }

  getSelectOptionName(options: IEditableSelectOption[], key: string, col: IEditableCol) {
    if (col.value) return this.getColValue(col, key);
    const target = options.find((option) => option.value === key);
    if (target) {
      return target.name;
    }
    return key;
  }

  getColValue(col: IEditableCol, val: string) {
    return col.value ? col.value(val) : val;
  }

  ngOnInit() {
    this.form = this.fb.group({});
    this.collectFormGroup();
    if (!this.editTransitions) {
      this.i18nSubscription = this.i18n.localeChange.subscribe(() => this.setTranslation());
    }
  }

  private setTranslation() {
    this.editTransitions = this.i18n.getLocaleData('editableTable') || {};
    this.addText = this.editTransitions.addText;
    this.cdr.markForCheck();
  }

  get isEditing() {
    return this.editRowIndex !== null;
  }

  private collectFormGroup() {
    if (this.cols) {
      this.cols.forEach((col) => {
        this.form.addControl(col.key, new FormControl(null, col.validators));
      });
    }
  }

  private checkValidity() {
    this.cols.forEach((col) => {
      if (col.editable !== false) {
        const control = this.form.get(col.key);
        if (control) {
          control.markAsDirty();
          control.updateValueAndValidity();
        }
      }
    });
    if (this.validate) {
      const compose = this.validate(this.form, this.rows, (this.isEditing) ? {
        ...this.editRow,
        ...this.form.value
      } : null);
      if (!compose) return;
      compose.forEach((item: IValidatorError) => {
        const control = this.form.controls[item.key];
        if (!control) {
          logger.warn('[@bixi/core/bixi-editable-table]', `校验 ${item.key} 未找到相对于控制器, 请查看校验返回数据是否和列配置对应！`);
        }
        control.setErrors({
          message: item.message
        });
      });
    }
  }

  onStartEdit(row: IEditableRow, index: number): void {
    if (this.isEditing && this.editDirty) {
      return; // 不可多行编辑
    }
    this.form.reset();
    this.cacheRow = { ...row };
    this.editRow = { ...row };
    this.editRowIndex = index;
    this.editChange.emit(true);
    this.form.patchValue(row);
    setTimeout(() => {
      if (this.firstEditingEle) {
        if (this.firstEditingEle instanceof NzSelectComponent) {
          this.firstEditingEle.focus();
        } else {
          (this.firstEditingEle.nativeElement as HTMLInputElement).focus();
        }
      }
    });
  }

  get editDirty() {
    return this.form.dirty;
  }

  reset() {
    this.editedRows = [];
    this.cancel();
  }

  private stopEdit(): void {
    this.editRowIndex = null;
    this.editRow = {};
    this.cacheRow = {};
    this.editChange.emit(false);
  }

  onConfirm() {
    if (!this.isEditing) return;
    this.checkValidity();
    if (!this.form.valid) {
      return;
    }
    if (this.isEditing) {
      const saveRow = {
        ...this.editRow,
        ...this.form.value
      };
      this.rows.splice(this.editRowIndex || 0, 1, saveRow);
      this.collectRowChange(saveRow);
      this.submitChange();
    }
    this.stopEdit();
  }

  cancel() {
    if (this.isEditing) {
      if (this.cacheRow) {
        this.rows.splice(this.editRowIndex || 0, 1, this.cacheRow);
      } else {
        // cacheRow 为空，表示此次取消是新建内容取消
        this.rows.splice(this.editRowIndex || 0, 1);
      }
      this.rows = [...this.rows];
      this.cdr.markForCheck();
      this.stopEdit();
    }
  }

  onClear(input: HTMLInputElement, key: string) {
    input.value = '';
    this.form.controls[key].markAsDirty();
    this.form.patchValue({
      [key]: ''
    });
    this.form.controls[key].updateValueAndValidity();
    this.onControlChange(input.value, key);
  }

  onControlChange(value: string, key: string) {
    // 无效的 valueChange（patchValue） 不触发
    if (typeof this.editRow === 'object' && !this.isRowEqual(this.editRow)) {
      this.editRow[key] = value;
      if (!value) return;
      const changedCol = this.cols.find(col => col.key === key);
      if (changedCol && changedCol.onChange) {
        changedCol.onChange(this.form, this.editRow);
      }
    }
  }

  private isRowEqual(change: { [key: string]: NzSafeAny; }): boolean {
    let rtn = true;
    if (Object.keys(change).length !== Object.keys(this.form.value).length) return false;
    Object.keys(change).forEach((key) => {
      if (this.form.get(key)?.value !== change[key]) {
        rtn = false;
      }
    });
    return rtn;
  }

  onDelete(index: number) {
    if (this.isEditing) {
      return;
    }
    this.collectRowChange({
      ...this.rows[index],
      __deleted: true
    });
    const row = this.rows.splice(index, 1);
    this.rows = [...this.rows];
    this.submitChange();
    this.deleteRow.emit(row);
  }

  private collectRowChange(row: IEditableRow) {
    if (row) {
      this.editedRows.push(row);
    }
  }

  submitChange() {
    this.dirtyRowsChange.emit(this.editedRows);
    this.onChange(this.rows);
  }

  drop(event: CdkDragDrop<IEditableRow[]>): void {
    moveItemInArray(this.rows, event.previousIndex, event.currentIndex);
    this.submitChange();
  }

  onAddRow() {
    if (this.isEditing) {
      return;
    }
    this.rows = [
      ...this.rows,
      {}
    ];
    this.onStartEdit({}, this.rows.length - 1);
    this.cacheRow = null;
    setTimeout(() => {
      // 因为 rows 变化渲染过程中会改变 form 中的值，这里等渲染完成后再赋值
      this.addRow.emit(this.form);
      const formValue = this.form.value;
      if (!this.isEmpty(formValue)) {
        // addRow 新增默认值后，设置为 dirty
        this.form.markAsDirty();
        // patchValue 无法改变 editable 为 false 的列。
        // 无法显示默认数据，这里去设置下 rows
        this.rows = [
          ...this.rows.slice(0, -1),
          {
            ...this.rows.pop(),
            ...this.form.value
          } || {}
        ];
      }
    });
  }

  writeValue(value: IEditableRow[]): void {
    this.rows = value;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: IOnChangeType): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: IOnTouchedType): void {
    this.onTouched = fn;
  }

  ngOnDestroy() {
    this.i18nSubscription.unsubscribe();
  }

  private isEmpty(value: {
    // tslint:disable-next-line: no-any
    [key: string]: any
  }): boolean {
    return !Object.values(value).find(val => val !== undefined);
  }
}
